import { Params } from '@angular/router';

/**
 * Where a lapsed or never-subscribed user goes to get a subscription back. Credits are a
 * subscriber feature end to end — all three credits endpoints refuse a user without an active
 * subscription — so every "you need a subscription" CTA in the app lands here.
 */
export const RESUBSCRIBE_ROUTE = '/user-discount';

export interface ResubscribeOptions {
  /**
   * The visitor's Peech uuid, when the app knows it. It travels as `appUserId`, never as
   * `id`: `id` is reserved for the AES-encrypted numeric user id that email-marketing links
   * carry, and the paywall feeds only that one to GET /users/identify (the endpoint decrypts
   * it, so a raw uuid comes back 400). A signed-in visitor is identified from the session
   * instead, and `appUserId` is only used to key analytics.
   */
  userId?: string;
  /** Which in-app CTA opened the paywall, e.g. 'libraryPremiumBanner'. */
  trigger: string;
  /**
   * The in-app placement that sent the user here (e.g. 'stream_subscription_modal'). The
   * discount paywall spreads every query param into `paywall_shown`, so
   * utm_source/utm_medium reach Amplitude without extra wiring.
   */
  utmMedium?: string;
  /**
   * Absolute in-app path to send the user back to once the purchase completes, so an app
   * user is not dead-ended on the app-store screen. Must start with a single '/' — the
   * paywall drops anything else rather than forwarding an off-site redirect.
   */
  redirect?: string;
}

/**
 * The email is deliberately left out so no personal data travels in a URL; the paywall reads
 * it from the signed-in session instead.
 */
export function resubscribeQueryParams({
  userId,
  trigger,
  utmMedium,
  redirect,
}: ResubscribeOptions): Params {
  const queryParams: Params = { trigger };

  if (userId) {
    queryParams['appUserId'] = userId;
  }

  if (redirect) {
    queryParams['redirect'] = redirect;
  }

  if (utmMedium) {
    queryParams['utm_source'] = 'web_app';
    queryParams['utm_medium'] = utmMedium;
  }

  return queryParams;
}

/**
 * Same-origin path guard for a `redirect` that arrived in a URL. A protocol-relative '//host'
 * is a cross-origin redirect in disguise, so only a single leading slash passes.
 */
export function safeInternalRedirect(value: unknown): string | undefined {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    ? value
    : undefined;
}
